import { Component, ViewEncapsulation, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  ICreateEmergencia,
  IEmergenciaMedica,
  IEventoGenerico,
  PrioridadEmergenciaMedica,
} from 'modelos/src';
import { CardHeaderComponent } from '../../../../../componentes/card-header/card-header.component';
import { ContentCardComponent } from '../../../../../componentes/content-card/content-card.component';
import { InfoBannerComponent } from '../../../../../componentes/info-banner/info-banner.component';
import { ButtonComponent } from '../../../../../auxiliares/button/button.component';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  EmergenciasService,
  IResultadoImportacionEmergencias,
} from '../../../../../auxiliares/servicios/http/emergencias.service';
import { presentacionPrioridad } from '../../estado-emergencia';

/** Columnas del archivo, en orden (separadas por punto y coma). */
export const COLUMNAS_IMPORTACION = [
  'Servicio',
  'Fecha',
  'Código',
  'Asignado',
  'Salida',
  'Llegada',
  'Llegada derivación',
  'Cierre',
  'Código de cierre',
  'Cancelado',
  'Reasignada',
  'Móvil reasignado',
  'Médico reasignado',
  'Enfermero reasignado',
  'Chofer reasignado',
  'Fecha asignado',
  'Fecha salida',
  'Fecha llegada',
  'Fecha derivación',
  'Fecha cierre',
] as const;

const CANTIDAD_COLUMNAS = COLUMNAS_IMPORTACION.length;
const I = {
  servicio: 0,
  fecha: 1,
  codigo: 2,
  codigoCierre: 8,
  cancelado: 9,
  reasignada: 10,
  fechaAsignado: 15,
  fechaSalida: 16,
  fechaLlegada: 17,
  fechaDerivacion: 18,
  fechaCierre: 19,
};
const COLUMNAS_FECHA = [
  I.fechaAsignado,
  I.fechaSalida,
  I.fechaLlegada,
  I.fechaDerivacion,
  I.fechaCierre,
];

const CODIGOS: Record<string, PrioridadEmergenciaMedica> = {
  VERDE: 'Verde',
  AMARILLO: 'Amarillo',
  ROJO: 'Rojo',
  NEGRO: 'Negro',
  CELESTE: 'Celeste',
};

const VERDADERO = ['verdadero', 'si', 'sí', 'true', '1', 'x'];
const FALSO = ['falso', 'no', 'false', '0', ''];

interface ILineaArchivo {
  /** Número de línea en el texto pegado o en el archivo. */
  numero: number;
  campos: string[];
}

/**
 * Importación de emergencias históricas (ya cerradas) desde un CSV. Sólo sirven
 * para las métricas: el servidor las crea con `importada: true`, en el estado
 * del archivo (Finalizada o Cancelada) y sin geocercas.
 */
@Component({
  selector: 'app-importar-emergencia',
  templateUrl: './importar.component.html',
  styleUrls: [
    './importar.component.scss',
    '../acciones-emergencia/acciones-emergencia-dialog.scss',
  ],
  encapsulation: ViewEncapsulation.None,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    CardHeaderComponent,
    ContentCardComponent,
    InfoBannerComponent,
    ButtonComponent,
  ],
})
export class ImportarEmergenciasComponent {
  private dialogRef = inject(MatDialogRef<ImportarEmergenciasComponent>);
  private helper = inject(HelperService);
  private emergenciasService = inject(EmergenciasService);

  public columnas = COLUMNAS_IMPORTACION;
  public codigos = Object.values(CODIGOS).map((p) => presentacionPrioridad(p));
  public primeraLineaEncabezado = true;
  public importando = false;
  public resultado?: IResultadoImportacionEmergencias & {
    total: number;
    erroresPorLinea: { linea: number; error: string }[];
  };

  public csv = new FormControl<string>('', [
    Validators.required,
    (c: AbstractControl) => this.validar(c.value),
  ]);

  // Lectura del texto

  public lineas(texto?: string | null): ILineaArchivo[] {
    const crudas = (texto ?? '').split(/\r?\n/);
    const lineas: ILineaArchivo[] = [];
    crudas.forEach((linea, i) => {
      if (!linea.trim()) return;
      if (
        this.primeraLineaEncabezado &&
        !lineas.length &&
        i === this.primeraNoVacia(crudas)
      ) {
        return;
      }
      lineas.push({
        numero: i + 1,
        campos: linea.split(';').map((c) => c.trim().replace(/^"+|"+$/g, '')),
      });
    });
    return lineas;
  }

  private primeraNoVacia(crudas: string[]): number {
    return crudas.findIndex((l) => !!l.trim());
  }

  public get cantidadLineas(): number {
    return this.lineas(this.csv.value).length;
  }

  private codigo(valor: string): PrioridadEmergenciaMedica | undefined {
    return CODIGOS[valor.trim().toUpperCase()];
  }

  private booleano(valor: string): boolean | undefined {
    const v = valor.trim().toLowerCase();
    if (VERDADERO.includes(v)) return true;
    if (FALSO.includes(v)) return false;
    return undefined;
  }

  /** "dd/mm/aaaa hh:mm:ss" (hora local) → ISO. */
  private fechaHora(valor: string): string | undefined {
    const m = valor
      .trim()
      .match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
    if (!m) return undefined;
    const [d, mes, a, h, min, s] = m.slice(1).map(Number);
    const fecha = new Date(a, mes - 1, d, h, min, s);
    const valida =
      fecha.getFullYear() === a &&
      fecha.getMonth() === mes - 1 &&
      fecha.getDate() === d &&
      fecha.getHours() === h &&
      fecha.getMinutes() === min;
    return valida ? fecha.toISOString() : undefined;
  }

  /** "dd/mm/aaaa" (hora local, 00:00) → ISO. Lo que siga a la fecha se ignora. */
  private fecha(valor: string): string | undefined {
    // Lo que siga a la fecha (una hora, por ejemplo) se ignora
    const m = valor.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (!m) return undefined;
    return this.fechaHora(`${m[0]} 00:00:00`);
  }

  private validar(texto?: string | null): ValidationErrors | null {
    const lineas = this.lineas(texto);
    if (!lineas.length) return null;
    for (const { numero, campos } of lineas) {
      const error = this.errorDeLinea(campos);
      if (error) return { error: `Línea ${numero}: ${error}` };
    }
    return null;
  }

  private errorDeLinea(campos: string[]): string | null {
    if (campos.length !== CANTIDAD_COLUMNAS) {
      return `tiene ${campos.length} columnas y tienen que ser ${CANTIDAD_COLUMNAS}`;
    }
    for (const i of COLUMNAS_FECHA) {
      const v = campos[i];
      if (v && !this.fechaHora(v)) {
        return `«${COLUMNAS_IMPORTACION[i]}» tiene que ser dd/mm/aaaa hh:mm:ss (dice «${v}»)`;
      }
    }
    for (const i of [I.codigo, I.codigoCierre]) {
      if (campos[i] && !this.codigo(campos[i])) {
        return `«${COLUMNAS_IMPORTACION[i]}» no es un código válido (dice «${campos[i]}»)`;
      }
    }
    if (!campos[I.codigo]) return 'falta el código de apertura';
    for (const i of [I.cancelado, I.reasignada]) {
      if (this.booleano(campos[i]) === undefined) {
        return `«${COLUMNAS_IMPORTACION[i]}» tiene que ser verdadero o falso (dice «${campos[i]}»)`;
      }
    }
    if (
      !campos[I.fechaAsignado] &&
      !campos[I.fecha] &&
      !campos[I.fechaCierre]
    ) {
      return 'no tiene ninguna fecha (Fecha, Fecha asignado o Fecha cierre)';
    }
    return null;
  }

  private aEmergencia(campos: string[]): ICreateEmergencia {
    const prioridadApertura = this.codigo(campos[I.codigo]);
    const prioridadCierre =
      this.codigo(campos[I.codigoCierre]) || prioridadApertura;
    const cancelada = this.booleano(campos[I.cancelado]) === true;
    const reasignada = this.booleano(campos[I.reasignada]) === true;

    const fechaPrimeraAsignacion = this.fechaHora(campos[I.fechaAsignado]);
    const fechaSalida = this.fechaHora(campos[I.fechaSalida]);
    const fechaLlegadaDestino = this.fechaHora(campos[I.fechaLlegada]);
    const fechaLlegadaHospital = this.fechaHora(campos[I.fechaDerivacion]);
    const fechaFinalizacion = this.fechaHora(campos[I.fechaCierre]);
    const fechaCreacion =
      fechaPrimeraAsignacion ||
      this.fecha(campos[I.fecha]) ||
      fechaFinalizacion;

    let emergenciaMedica: IEmergenciaMedica | undefined;
    if (fechaLlegadaHospital) {
      emergenciaMedica = { irAHospital: true, fechaLlegadaHospital };
    }

    const ultimoEventoEmergencia: IEventoGenerico = {
      estado: cancelada ? 'Cancelada' : 'Finalizada',
      fechaCreacion: fechaFinalizacion ?? fechaCreacion,
    };

    const servicio = campos[I.servicio];
    return {
      tipo: 'Médica',
      prioridadApertura,
      prioridadCierre,
      fechaCreacion,
      fechaPrimeraAsignacion,
      fechaSalida,
      fechaLlegadaDestino,
      fechaFinalizacion,
      cantidadReasignaciones: reasignada ? 1 : undefined,
      asignada: !!fechaPrimeraAsignacion,
      emergenciaMedica,
      observaciones: servicio ? `Servicio ${servicio}` : undefined,
      ultimoEventoEmergencia,
    };
  }

  // Acciones

  public async cargarArchivo(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const archivo = input.files?.[0];
    if (!archivo) return;
    const texto = await archivo.text();
    this.csv.setValue(texto);
    this.csv.markAsTouched();
    input.value = '';
  }

  public revalidar(): void {
    this.csv.updateValueAndValidity();
  }

  public async importar(): Promise<void> {
    if (this.csv.invalid || this.importando) return;
    const lineas = this.lineas(this.csv.value);
    const datos = lineas.map((l) => this.aEmergencia(l.campos));
    this.importando = true;
    try {
      const res = await this.emergenciasService.importar(datos);
      this.resultado = {
        ...res,
        total: datos.length,
        erroresPorLinea: (res.errores ?? []).map((e) => ({
          linea: lineas[e.fila - 1]?.numero ?? e.fila,
          error: e.error,
        })),
      };
      if (res.creadas && !res.errores?.length) {
        this.helper.notifSuccess(
          `Se importaron ${res.creadas.toLocaleString('es-AR')} emergencias`,
        );
      } else if (res.creadas) {
        this.helper.notifWarn(
          `Se importaron ${res.creadas.toLocaleString('es-AR')} de ${datos.length.toLocaleString('es-AR')} emergencias`,
        );
      } else {
        this.helper.notifError('No se importó ninguna emergencia');
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.importando = false;
  }

  public otraImportacion(): void {
    this.resultado = undefined;
    this.csv.setValue('');
    this.csv.markAsUntouched();
  }

  public cerrar(): void {
    this.dialogRef.close(this.resultado?.creadas ?? 0);
  }
}
